import styled from '@emotion/styled';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming';
import { shouldForwardProp } from '../../utils/emotion';
import { forwardRef, useContext } from 'react';

// Custom Bar Chart Icon Component
const BarChartIcon = ({
  height = 20,
  width = 20,
  ...props
}: {
  height?: number;
  width?: number;
  [key: string]: unknown;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    height={height}
    viewBox="0 -960 960 960"
    width={width}
    fill="currentColor"
    {...props}>
    <path d="M640-160v-280h160v280H640Zm-240 0v-640h160v640H400Zm-240 0v-440h160v440H160Z" />
  </svg>
);

// Custom Table Icon Component
const TableIcon = ({
  height = 20,
  width = 20,
  ...props
}: {
  height?: number;
  width?: number;
  [key: string]: unknown;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    height={height}
    viewBox="0 -960 960 960"
    width={width}
    fill="currentColor"
    {...props}>
    <path d="M760-120H200q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120ZM200-640h560v-120H200v120Zm100 80H200v360h100v-360Zm360 0v360h100v-360H660Zm-80 0H380v360h200v-360Z" />
  </svg>
);

type ViewMode = 'chart' | 'table';

type Props = {
  onClick?: () => void;
  disabled?: boolean;
  viewMode: ViewMode;
  isActive: boolean;
};

export const ChartViewButton = forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const themingContext = useContext(AppThemingContext);

  const enabled = themingContext?.enabled;
  const color = enabled ? themingContext.palette.primary.main : null;
  const hasCustomColor = !!color;

  return (
    <Button
      ref={ref}
      onClick={props.disabled ? undefined : props.onClick}
      aria-label="Chart View"
      type="button"
      disabled={props.disabled}
      className={props.isActive ? 'active' : ''}
      $isActive={props.isActive}
      $hasCustomColor={hasCustomColor}
      $themeColor={color}>
      <IconContainer
        $isActive={props.isActive}
        $hasCustomColor={hasCustomColor}>
        <StyledChartIcon
          height={20}
          width={20}
          $isActive={props.isActive}
          $hasCustomColor={hasCustomColor}
          $themeColor={color} />
      </IconContainer>
    </Button>
  );
});

ChartViewButton.displayName = 'ChartViewButton';

export const TableViewButton = forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const themingContext = useContext(AppThemingContext);

  const enabled = themingContext?.enabled;
  const color = enabled ? themingContext.palette.primary.main : null;
  const hasCustomColor = !!color;

  return (
    <Button
      ref={ref}
      onClick={props.disabled ? undefined : props.onClick}
      aria-label="Table View"
      type="button"
      disabled={props.disabled}
      className={props.isActive ? 'active' : ''}
      $isActive={props.isActive}
      $hasCustomColor={hasCustomColor}
      $themeColor={color}>
      <IconContainer
        $isActive={props.isActive}
        $hasCustomColor={hasCustomColor}>
        <StyledTableIcon
          height={20}
          width={20}
          $isActive={props.isActive}
          $hasCustomColor={hasCustomColor}
          $themeColor={color} />
      </IconContainer>
    </Button>
  );
});

TableViewButton.displayName = 'TableViewButton';

const Button = styled('button', {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor', '$themeColor'),
})<{
  $isActive?: boolean;
  $hasCustomColor?: boolean;
  $themeColor?: string;
}>(({ theme, $isActive, $hasCustomColor, $themeColor }) => {
  const borderColor = $hasCustomColor ? 'var(--gray-m)' : 'var(--gray-m)';
  const backgroundColor = $hasCustomColor ? 'var(--gray-xl)' : (theme?.palette?.white?.main || '#fff');
  const textColor = $hasCustomColor ? 'var(--gray-d)' : 'var(--gray-d)';

  return {
    display: 'flex',
    alignItems: 'center',
    background: backgroundColor,
    padding: '8px 8px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 500,
    fontSize: '14px',
    transition: 'all 0.2s ease',
    outline: 'none',
    borderRadius: '6px',
    color: textColor,
    whiteSpace: 'nowrap',
    border: `1px solid ${borderColor}`,
    '&.active': {
      background: $hasCustomColor ? 'var(--gray-l)' : 'var(--blue-l)',
      borderColor: $hasCustomColor ? 'var(--gray-d)' : 'var(--blue-m)',
      color: $hasCustomColor ? 'var(--gray-d)' : 'var(--blue-d)',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      color: 'var(--gray-l)',
      boxShadow: 'none',
      '&:active, &.active': {
        background: 'none',
        color: 'inherit',
      },
    },
    '&:hover:not(:disabled):not(.active)': {
      background: $hasCustomColor ? 'var(--gray-l)' : 'var(--gray-xl)',
      borderColor: $hasCustomColor ? 'var(--gray-m)' : 'var(--gray-m)',
    },
  };
});

const StyledChartIcon = styled(BarChartIcon, {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor', '$themeColor'),
})<{
  $isActive?: boolean;
  $hasCustomColor?: boolean;
  $themeColor?: string;
}>(({ $isActive, $hasCustomColor, $themeColor }) => {
  const iconColor = $hasCustomColor ? 'var(--gray-d)' : 'var(--gray-d)';

  return {
    color: iconColor,
    fill: iconColor,
  };
});

const StyledTableIcon = styled(TableIcon, {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor', '$themeColor'),
})<{
  $isActive?: boolean;
  $hasCustomColor?: boolean;
  $themeColor?: string;
}>(({ $isActive, $hasCustomColor, $themeColor }) => {
  const iconColor = $hasCustomColor ? 'var(--gray-d)' : 'var(--gray-d)';

  return {
    color: iconColor,
    fill: iconColor,
  };
});

const IconContainer = styled('span', {
  shouldForwardProp: shouldForwardProp('$isActive', '$hasCustomColor'),
})<{
  $isActive?: boolean;
  $hasCustomColor?: boolean;
}>(({ $isActive, $hasCustomColor }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    height: '20px',
    paddingRight: '0px',
    marginRight: '0px',
  };
});
